import { useNavigate } from "@tanstack/react-router";
import { LEAD_LIMITS, type AdminLeadRun } from "@teamna/shared";
import { useState } from "react";
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  ErrorState,
  PageHeader,
  Panel,
  TextAreaField,
  showToast,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { useDeleteLeadRun, useLeadRunDeletePreview, useLeadRuns, useSaveLeadRunNote } from "../lib/leads-api.ts";
import { LeadsTabs, runTitle } from "./leads-parts.tsx";
import { lastActivityText } from "./format.ts";

/**
 * The runs, `/leads/runs`: each small run Claude did from the laptop, with what it searched, the
 * counts, its report and a note. A run opens its leads in the table. Delete run deletes the leads
 * the run made, except the ones a person changed or marked Do not contact.
 */

const onError = (error: Error) => showToast(copyForError(error).message, { tone: "info" });

function RunState({ run }: { run: AdminLeadRun }) {
  if (run.deletedAt) return <Badge tone="cancelled">Deleted</Badge>;
  if (run.finishedAt) return <Badge tone="confirmed">Finished</Badge>;
  return <Badge tone="info">Running</Badge>;
}

const COLUMNS: DataTableColumn<AdminLeadRun>[] = [
  {
    id: "run",
    header: "Run",
    sortValue: (row) => row.number,
    cell: (row) => (
      <div className="min-w-[180px]">
        <p className="font-semibold text-ink">{runTitle(row)}</p>
        <p className="text-[12px] text-ink-2" dir="auto">
          {[row.area, row.searches.join(", ")].filter(Boolean).join(" · ")}
        </p>
      </div>
    ),
  },
  {
    id: "saved",
    header: "Leads",
    align: "end",
    sortValue: (row) => row.leadCount,
    cell: (row) => (
      <span className="tabular-nums">
        {row.leadCount} of {row.stopAt}
      </span>
    ),
  },
  { id: "read", header: "Read", align: "end", hideOnPhone: true, sortValue: (row) => row.profilesRead, cell: (row) => <span className="tabular-nums">{row.profilesRead ?? "-"}</span> },
  {
    id: "started",
    header: "Started",
    hideOnPhone: true,
    sortValue: (row) => row.startedAt,
    cell: (row) => (
      <div className="text-[12px] text-ink-2">
        <p>{lastActivityText(row.startedAt)}</p>
        <p>{row.startedBy}</p>
      </div>
    ),
  },
  { id: "state", header: "State", cell: (row) => <RunState run={row} /> },
];

export function LeadRunsScreen() {
  const runs = useLeadRuns();
  const [openId, setOpenId] = useState<string | null>(null);
  const open = runs.data?.find((run) => run.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Leads"
        description={`Small runs from your laptop: one sport, one emirate, one source, at most ${LEAD_LIMITS.runMaxLeads} leads. Ask Claude for a run, then check its leads here.`}
      />
      <LeadsTabs current="runs" />
      {runs.error ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={runs.data ?? []}
          rowKey={(row) => row.id}
          rowLabel={(row) => `Run #${row.number}`}
          onRowClick={(row) => setOpenId(row.id)}
          isLoading={runs.isLoading}
          ranked
          countLabel={(count) => `${count} ${count === 1 ? "run" : "runs"}`}
          empty={{
            title: "No runs yet",
            message: 'Connect Claude (Leads, Settings), then ask it on your laptop: "Run a small swim run in Dubai on Instagram, stop at 40."',
          }}
        />
      )}
      <Panel open={open !== null} onClose={() => setOpenId(null)} title={open ? `Run ${runTitle(open)}` : "Run"} width="lg">
        {open ? <RunDetail key={open.id} run={open} onDeleted={() => setOpenId(null)} /> : null}
      </Panel>
    </>
  );
}

function RunDetail({ run, onDeleted }: { run: AdminLeadRun; onDeleted: () => void }) {
  const navigate = useNavigate();
  const saveNote = useSaveLeadRunNote();
  const remove = useDeleteLeadRun();
  const [note, setNote] = useState(run.note ?? "");
  const [confirming, setConfirming] = useState(false);
  const preview = useLeadRunDeletePreview(confirming ? run.id : null);

  return (
    <div className="flex flex-col gap-5">
      <dl className="grid grid-cols-2 gap-3 text-[14px]">
        <div>
          <dt className="text-[12px] text-ink-2">Searches</dt>
          <dd dir="auto">{run.searches.join(", ")}</dd>
        </div>
        <div>
          <dt className="text-[12px] text-ink-2">Area</dt>
          <dd dir="auto">{run.area ?? run.emirate}</dd>
        </div>
        <div>
          <dt className="text-[12px] text-ink-2">Leads saved</dt>
          <dd className="tabular-nums">
            {run.leadCount} of {run.stopAt}
          </dd>
        </div>
        <div>
          <dt className="text-[12px] text-ink-2">Profiles read, skipped</dt>
          <dd className="tabular-nums">
            {run.profilesRead ?? "-"}, {run.skipped ?? "-"}
          </dd>
        </div>
      </dl>
      {run.report ? (
        <section>
          <h3 className="text-[14px] font-semibold text-ink">Claude's report</h3>
          <p className="text-[14px] whitespace-pre-line text-ink-2" dir="auto">
            {run.report}
          </p>
        </section>
      ) : null}
      <div className="flex flex-col gap-2">
        <TextAreaField label="Your note" hint="For example: labels fine, a phone found on only half." rows={3} value={note} onChange={(event) => setNote(event.target.value)} dir="auto" />
        <div>
          <Button variant="secondary" size="sm" loading={saveNote.isPending} onClick={() => saveNote.mutate({ runId: run.id, note: note.trim() || null }, { onSuccess: () => showToast("Note saved."), onError })}>
            Save note
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void navigate({ to: "/leads", search: { run: run.id } as never })}>Open the leads of this run</Button>
        {!run.deletedAt ? (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Delete run
          </Button>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirming}
        title={`Delete the leads of run #${run.number}?`}
        confirmLabel="Delete run"
        tone="danger"
        loading={remove.isPending}
        confirmDisabled={!preview.data}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          remove.mutate(run.id, {
            onSuccess: () => {
              setConfirming(false);
              showToast(`Run #${run.number} deleted.`);
              onDeleted();
            },
            onError,
          })
        }
      >
        {preview.data ? (
          <div className="flex flex-col gap-2">
            <p>
              {preview.data.willDelete} {preview.data.willDelete === 1 ? "lead goes" : "leads go"}, with their numbers and labels.
            </p>
            {preview.data.kept.length ? (
              <p>
                {preview.data.kept.length} {preview.data.kept.length === 1 ? "lead stays" : "leads stay"}, because a person changed {preview.data.kept.length === 1 ? "it" : "them"}:{" "}
                {preview.data.kept.map((lead) => lead.name).join(", ")}.
              </p>
            ) : null}
          </div>
        ) : (
          <p>Counting the leads of this run.</p>
        )}
      </ConfirmDialog>
    </div>
  );
}
